import { useEffect, useState } from 'react';
import { Button, Input, Modal, Select } from '@react-apps/design-system';
import { TENANT_OPTIONS, CONTRACT_STATUS_OPTIONS } from '../shared/display';
import { ContactSearchInput } from '@/shared/contact-search/ContactSearchInput';
import {
  EMPTY_CONTRACT_FILTERS,
  type ContractFilters,
} from '../shared/contracts-filters';

const BU_FILTER_OPTIONS = [
  { value: '', label: 'Todas as BUs' },
  ...TENANT_OPTIONS,
];

const STATUS_FILTER_OPTIONS = [
  // Sem status, o back esconde as tentativas não convertidas.
  { value: '', label: 'Todos (exceto não convertidos)' },
  ...CONTRACT_STATUS_OPTIONS,
];

interface ContractsFiltersModalProps {
  open: boolean;
  onClose: () => void;
  filters: ContractFilters;
  onApply: (filters: ContractFilters) => void;
}

export function ContractsFiltersModal({
  open,
  onClose,
  filters,
  onApply,
}: ContractsFiltersModalProps) {
  const [draft, setDraft] = useState(filters);
  /**
   * O termo digitado na busca de contato vive aqui, e não dentro do
   * `ContactSearchInput`: o `Modal` desmonta os filhos ao fechar
   * (`if (!open) return null`), então estado interno lá seria perdido a cada
   * fechamento — enquanto os demais campos sobrevivem no `draft`.
   *
   * Diferente do `draft`, **não** é resetado ao reabrir: é texto de busca, não
   * valor de filtro (só a seleção vira filtro), então descartá-lo não é o que
   * "Cancelar" quer dizer — é só perder o que a pessoa digitou.
   */
  const [contactTerm, setContactTerm] = useState('');

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const update = <K extends keyof ContractFilters>(
    key: K,
    value: ContractFilters[K],
  ) => setDraft((prev) => ({ ...prev, [key]: value }));

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Filtrar contratos"
      description="Todos os filtros rodam no servidor, sobre a base inteira de contratos."
      size="lg"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft(EMPTY_CONTRACT_FILTERS);
              setContactTerm('');
            }}
          >
            Limpar
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleApply}>
            Aplicar filtros
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <ContactSearchInput
          value={draft.contactUserId || null}
          onChange={(userId) =>
            setDraft((prev) => ({ ...prev, contactUserId: userId ?? '' }))
          }
          term={contactTerm}
          onTermChange={setContactTerm}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Contrato ID"
            placeholder="Filtrar por ID do contrato..."
            value={draft.contractId}
            onChange={(e) => update('contractId', e.target.value)}
          />
          <Input
            label="Nome do produto"
            placeholder="Filtrar por produto..."
            value={draft.product}
            onChange={(e) => update('product', e.target.value)}
          />
          <Select
            label="BU"
            id="contracts-filter-bu"
            options={BU_FILTER_OPTIONS}
            value={draft.tenant}
            onChange={(e) =>
              update('tenant', e.target.value as ContractFilters['tenant'])
            }
          />
          <Select
            label="Status"
            id="contracts-filter-status"
            options={STATUS_FILTER_OPTIONS}
            value={draft.status}
            onChange={(e) =>
              update('status', e.target.value as ContractFilters['status'])
            }
          />
        </div>

        <div>
          <p className="text-(--color-text-primary) mb-2 text-sm font-medium">
            Iniciado em
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="De"
              type="date"
              value={draft.startedFrom}
              onChange={(e) => update('startedFrom', e.target.value)}
            />
            <Input
              label="Até"
              type="date"
              value={draft.startedTo}
              onChange={(e) => update('startedTo', e.target.value)}
            />
          </div>
        </div>

        <div>
          <p className="text-(--color-text-primary) mb-2 text-sm font-medium">
            Encerrado em
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="De"
              type="date"
              value={draft.endedFrom}
              onChange={(e) => update('endedFrom', e.target.value)}
            />
            <Input
              label="Até"
              type="date"
              value={draft.endedTo}
              onChange={(e) => update('endedTo', e.target.value)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
